/**
 * Poker utilities for Poker Reborn
 *
 * Hand rankings, card utilities, and game helpers.
 *
 * @see docs/screens/room/GAME_END.md - Hand name display requirements
 */

// =============================================================================
// HAND RANKINGS
// =============================================================================

/**
 * Maps hand_id from server to display name.
 * Protocol sends hand_id as 0-9 based on hand strength.
 */
export const HAND_NAMES: Record<number, string> = {
    1: 'High Card',
    2: 'One Pair',
    3: 'Two Pair',
    4: 'Three of a Kind',
    5: 'Straight',
    6: 'Flush',
    7: 'Full House',
    8: 'Four of a Kind',
    9: 'Straight Flush',
    10: 'Royal Flush',
};

/**
 * Maps hand_id to the number of "core" cards that define the hand.
 * Core cards get strong highlight, remaining cards (kickers) get subtle highlight.
 *
 * Example: One Pair (K-K-A-Q-J) has 2 core cards (the Kings),
 * the remaining 3 are kickers that don't define the hand type.
 */
export const CORE_CARD_COUNT: Record<number, number> = {
    1: 1, // High Card - just the high card
    2: 2, // One Pair - the pair
    3: 4, // Two Pair - both pairs
    4: 3, // Three of a Kind - the trips
    5: 5, // Straight - all 5 cards form the sequence
    6: 5, // Flush - all 5 cards form the flush
    7: 5, // Full House - trips + pair = all 5
    8: 4, // Four of a Kind - the quads
    9: 5, // Straight Flush - all 5
    10: 5, // Royal Flush - all 5
};

/**
 * Get the display name for a hand_id
 * @param handId - The hand_id from the server (0-9)
 * @returns The display name or undefined if invalid
 */
export function getHandName(handId: number): string | undefined {
    return HAND_NAMES[handId];
}

/**
 * Get the number of core cards for a hand type.
 * Core cards are those that define the hand (e.g., the pair in One Pair).
 * Remaining cards up to 5 are kickers.
 *
 * @param handId - The hand_id from the server (1-10)
 * @returns Number of core cards (defaults to 5 if unknown hand)
 */
export function getCoreCardCount(handId: number): number {
    return CORE_CARD_COUNT[handId] ?? 5;
}

/**
 * Check if a hand_id represents a valid hand (not just high card from fold)
 * @param handId - The hand_id from the server
 * @returns true if this is a real showdown hand
 */
export function isValidShowdownHand(handId: number): boolean {
    return handId >= 0 && handId <= 9;
}
